<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import StandardSelect from '@/components/StandardSelect.vue';
import ReorderableTable from '@/components/ReorderableTable.vue';
import { Constants } from '@/constants';
import type { FeedbackEntryDto, Locales, ProjectModuleDto } from '@/services/types/api.types';
import { useFeedbackEntriesStore } from '@/stores/feedbackEntries.store';
import FeedbackEntryDialog from '@/components/dialogs/FeedbackEntryDialog.vue';
import { getFullName, getMaxLengthText } from '@/helpers/text.helper';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const feedbackEntriesStore = useFeedbackEntriesStore();
const { feedbackEntryList } = storeToRefs(feedbackEntriesStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

// REFS
const statusFilter = ref<string>(Constants.ProjectModule.FeedbackEntry.Status.Pending.toString());

// COMPUTED
const rows = computed(() => {
  if (!statusFilter.value) return feedbackEntryList.value;
  return feedbackEntryList.value.filter((entry) => entry.status.toString() === statusFilter.value);
});
const columns = computed(() => [
  {
    name: 'name',
    label: t('global.label.name'),
    field: (row: FeedbackEntryDto) => getFullName(row.user),
    align: 'left',
  },
  {
    name: 'message',
    label: t('global.label.message'),
    field: (row: FeedbackEntryDto) => getMaxLengthText(row.message),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: FeedbackEntryDto) => getStatusLabel(row.status),
    align: 'left',
  },
]);
const options = computed(() => [
  { label: t('project-module.feedback.status.all'), value: '' },
  {
    label: t('project-module.feedback.status.pending'),
    value: Constants.ProjectModule.FeedbackEntry.Status.Pending.toString(),
  },
  {
    label: t('project-module.feedback.status.approved'),
    value: Constants.ProjectModule.FeedbackEntry.Status.Approved.toString(),
  },
  {
    label: t('project-module.feedback.status.refused'),
    value: Constants.ProjectModule.FeedbackEntry.Status.Refused.toString(),
  },
]);

// LIFECYCLE
onMounted(async () => {
  if (props.module.id) {
    await feedbackEntriesStore.fetchFeedbackEntriesByModuleId(props.module.id);
  }
});

// FUNCTIONS
const getStatusLabel = (status: number) => {
  switch (status) {
    case Constants.ProjectModule.FeedbackEntry.Status.Pending:
      return t('project-module.feedback.status.pending');
    case Constants.ProjectModule.FeedbackEntry.Status.Approved:
      return t('project-module.feedback.status.approved');
    case Constants.ProjectModule.FeedbackEntry.Status.Refused:
      return t('project-module.feedback.status.refused');
    default:
      return '';
  }
};

const handleDeleteRow = (row: FeedbackEntryDto) => {
  $q.dialog({
    title: t('global.dialogs.deleteTitle', { type: t('project-module.feedback.name') }),
    message: t('global.dialogs.deleteMessage', { type: t('project-module.feedback.name') }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await feedbackEntriesStore.deleteFeedbackEntry(row);
  });
};

const handleRowClick = (row: FeedbackEntryDto) => {
  $q.dialog({
    component: FeedbackEntryDialog,
    componentProps: {
      feedbackEntry: row,
      languageEnabled: props.languageEnabled,
    },
  });
};
</script>
<template>
  <div class="feedback-list">
    <reorderable-table
      :rows="rows"
      :columns="columns"
      :title="t('project-module.feedback.title')"
      itemKey="id"
      :disable-add-button="true"
      :disable-orderable="true"
      @delete-row="handleDeleteRow"
      @row-click="handleRowClick"
    >
      <template v-slot:filters>
        <standard-select
          name="statusFilter"
          :label="t('global.label.status')"
          class="filters"
          v-model="statusFilter"
          :options="options"
          emit-value
          map-options
          outlined
          hide-bottom-space
        />
      </template>
    </reorderable-table>
  </div>
</template>
<style lang="scss">
.feedback-list {
  .filters {
    padding: 10px 0 0 0;
    width: 200px;
  }
}
</style>
